import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Types
export interface Service {
  id: string;
  serviceTypeName: string;
  serviceTypeDescription: string;
  serviceTypePrice: number;
  duration: number;
  dateCreated: string;
  serviceOrder: number;
  serviceTypeIcon: string;
  serviceCategory: string;
  showOnBookingPage: boolean;
}

export interface WorkDay {
  workDay: number;
  startTime: string;
  endTime: string;
  isWorkDay: boolean;
}

export interface ThemeData {
  colors?: ThemeColors;
}

export interface ThemeColors {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
}

export interface LogoData {
  logoUrl?: string;
}

export interface BusinessImages {
  imageUrl?: string;
}

export interface NotesField {
  required: boolean;
  title: string;
  description: string;
}

export interface BookingData {
  businessId: string;
  businessName: string;
  contactName: string;
  phoneNumber: string;
  services: Service[];
  workSchedule: WorkDay[];
  themeData?: ThemeData;
  logoData?: LogoData;
  businessImages?: BusinessImages[];
  businessDescription?: string;
  notesField?: NotesField | null;
}

export interface TimeSlotData {
  timeSlots: string[];
}

export interface BookingRequest {
  businessId: string;
  services: Service[];
  date: string;
  time: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerLocation: string;
  notes?: string;
  timeZone: string;
}

export interface BookingResponse {
  bookingId: string;
  confirmationNumber: string;
  status: string;
}

export interface SendMessageRequest {
  businessId: string;
  senderEmail: string;
  senderName: string;
  senderMessage: string;
}

export interface SendMessageResponse {
  success: boolean;
}

export interface BookedSlot {
  date: string;
  startTime: string;
  endTime: string;
}

export interface GetBookedSlotsRequest {
  businessId: string;
  startDate: string;
  endDate: string;
}

export interface GetBookedSlotsResponse {
  bookedSlots: BookedSlot[];
}

// Service functions
export async function getBookingDataBySlug(slug: string): Promise<BookingData> {
  const fn = httpsCallable(functions, 'getBookingDataBySlug_v3');
  
  try {
    const result = await fn({ slug });
    return result.data as BookingData;
  } catch (error: any) {
    if (error.code === 'functions/failed-precondition') {
      throw new Error('BUSINESS_DISABLED');
    }
    console.error('Error fetching booking data:', error);
    throw new Error('BUSINESS_NOT_FOUND');
  }
}

export async function getAvailableTimeSlots(
  businessId: string,
  date: string,
  duration: number,
  timeZone: string
): Promise<string[]> {
  const fn = httpsCallable(functions, 'getAvailableTimeSlots');
  
  try {
    const result = await fn({
      businessId,
      date,
      duration,
      timeZone
    });
    
    const data = result.data as TimeSlotData;
    return data.timeSlots;
  } catch (error) {
    console.error('Error fetching time slots:', error);
    throw new Error('Failed to load available time slots');
  }
}

export async function bookAppointment(bookingRequest: BookingRequest): Promise<BookingResponse> {
  const fn = httpsCallable(functions, 'bookAppointment_v2');
  
  try {
    // Map frontend parameters to Firebase function expected parameter names
    const firebaseParams = {
      businessId: bookingRequest.businessId,
      name: bookingRequest.customerName,
      phone: bookingRequest.customerPhone,
      email: bookingRequest.customerEmail,
      date: bookingRequest.date,
      time: bookingRequest.time,
      services: bookingRequest.services,
      location: bookingRequest.customerLocation,
      timeZone: bookingRequest.timeZone,
      notes: bookingRequest.notes || ''
    };
    
    const result = await fn(firebaseParams);
    return result.data as BookingResponse;
  } catch (error: any) {
    console.error('Error booking appointment:', error);
    
    if (error.code === 'functions/failed-precondition') {
      throw new Error('TIME_SLOT_UNAVAILABLE');
    }
    
    // Handle time overlap error
    if (error.message && error.message.includes('overlaps with an existing appointment')) {
      throw new Error('TIME_SLOT_UNAVAILABLE');
    }
    
    throw new Error('BOOKING_FAILED');
  }
}

export async function sendMessage(messageRequest: SendMessageRequest): Promise<SendMessageResponse> {
  const fn = httpsCallable(functions, 'sendMessage');
  
  try {
    const result = await fn({
      businessId: messageRequest.businessId,
      senderEmail: messageRequest.senderEmail,
      senderName: messageRequest.senderName,
      senderMessage: messageRequest.senderMessage
    });
    
    return result.data as SendMessageResponse;
  } catch (error: any) {
    console.error('Error sending message:', error);
    throw new Error('MESSAGE_SEND_FAILED');
  }
}

export async function getBookedSlots(
  businessId: string,
  startDate: string,
  endDate: string
): Promise<BookedSlot[]> {
  const fn = httpsCallable(functions, 'getBookedSlots_v2');
  
  try {
    const result = await fn({
      businessId,
      startDate,
      endDate
    });
    
    const data = result.data as GetBookedSlotsResponse;
    return data.bookedSlots;
  } catch (error) {
    console.error('Error fetching booked slots:', error);
    throw new Error('Failed to load booked slots');
  }
}

// Helper function to parse time string (supports both 12-hour and 24-hour formats)
function parseTimeToMinutes(timeString: string): number {
  // Check if it's 12-hour format (contains AM/PM)
  if (timeString.includes('AM') || timeString.includes('PM')) {
    const isPM = timeString.includes('PM');
    const [time] = timeString.split(' ');
    const [hourStr, minuteStr] = time.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    
    // Convert to 24-hour format
    if (isPM && hour !== 12) {
      hour += 12;
    } else if (!isPM && hour === 12) {
      hour = 0;
    }
    
    return hour * 60 + minute;
  } else {
    // 24-hour format
    const [hour, minute] = timeString.split(':').map(Number);
    return hour * 60 + minute;
  }
}

// Utility function to calculate available time slots from booked slots
export function calculateAvailableTimeSlots(
  date: string,
  bookedSlots: BookedSlot[],
  workSchedule: WorkDay[],
  duration: number
): string[] {
  // Get the day of week (0 = Sunday, 6 = Saturday)
  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.getDay();
  
  // Find work schedule for this day
  const workDay = workSchedule.find(w => w.workDay === dayOfWeek && w.isWorkDay);
  if (!workDay) {
    return []; // Not a work day
  }
  
  // Get booked slots for this specific date
  const dayBookedSlots = bookedSlots.filter(slot => slot.date === date);
  
  // Parse work hours (24-hour format)
  const [startHour, startMinute] = workDay.startTime.split(':').map(Number);
  const [endHour, endMinute] = workDay.endTime.split(':').map(Number);
  
  const workStartMinutes = startHour * 60 + startMinute;
  const workEndMinutes = endHour * 60 + endMinute;
  
  // Generate all possible time slots (15-minute intervals)
  const availableSlots: string[] = [];
  
  for (let minutes = workStartMinutes; minutes + duration <= workEndMinutes; minutes += 15) {
    const slotEndMinutes = minutes + duration;
    
    // Check if this slot conflicts with any booked slot
    const hasConflict = dayBookedSlots.some(booked => {
      const bookedStartMinutes = parseTimeToMinutes(booked.startTime);
      const bookedEndMinutes = parseTimeToMinutes(booked.endTime);
      
      // Check for overlap: slot starts before booked ends AND slot ends after booked starts
      return minutes < bookedEndMinutes && slotEndMinutes > bookedStartMinutes;
    });
    
    if (!hasConflict) {
      // Convert minutes back to time string (12-hour format)
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const timeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
      
      availableSlots.push(timeString);
    }
  }
  
  return availableSlots;
}

// Utility function to get dates that have at least one available slot
export function getAvailableDates(
  bookedSlots: BookedSlot[],
  workSchedule: WorkDay[],
  duration: number,
  startDate: string,
  endDate: string
): string[] {
  const availableDates: string[] = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  
  // Iterate through each date in range
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateString = d.toISOString().split('T')[0];
    const slots = calculateAvailableTimeSlots(dateString, bookedSlots, workSchedule, duration);
    
    if (slots.length > 0) {
      availableDates.push(dateString);
    }
  }
  
  return availableDates;
}
